import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Sidebar as ShadcnSidebar,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarGroupContent,
} from '@/components/ui/sidebar';
import { Home, Clock, BarChart2, Building2, Briefcase, Settings, Wallet, User, CreditCard, FileCheck, ShieldCheck } from 'lucide-react';
import { SentraMark, SentraWordmark as BrandWordmark } from '@/components/brand/SentraMark';

/** Sentra mark as a nav icon (single colour, follows text colour). */
const SentraAIIcon = ({ className }: { className?: string }) => (
  <SentraMark size={20} className={className} />
);

const SentraWordmark = () => <BrandWordmark tone="dark" size={28} className="px-3 pt-3 pb-4" />;
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/hooks/useAuth';
import { ROLES, type RoleId, hasAnyRole } from '@/lib/permissions';
import { cn } from '@/lib/utils';

interface MenuItem {
  title: string;
  url: string;
  icon: any;
  visibleForGroups: RoleId[];
  requiresDomain?: string;
}

const MENU_ITEMS: MenuItem[] = [
  {
    title: 'Home',
    url: 'dashboard',
    icon: Home,
    visibleForGroups: [ROLES.SUPER_USER, ROLES.ADMIN, ROLES.ACCOUNTING, ROLES.HR, ROLES.MANAGER, ROLES.REGULAR],
  },
  {
    // Mirrors the backend AI_ASSISTANT_ALLOWED_GROUPS gate
    title: 'Sentra AI',
    url: 'ai',
    icon: SentraAIIcon,
    visibleForGroups: [ROLES.SUPER_USER],
  },
  {
    title: 'Timesheets',
    url: 'timesheets',
    icon: Clock,
    visibleForGroups: [ROLES.SUPER_USER, ROLES.ADMIN, ROLES.ACCOUNTING, ROLES.HR, ROLES.MANAGER, ROLES.REGULAR],
  },
  {
    title: 'Statistics',
    url: 'statistics',
    icon: BarChart2,
    visibleForGroups: [ROLES.SUPER_USER, ROLES.ADMIN, ROLES.ACCOUNTING, ROLES.MANAGER],
  },
  {
    title: 'Invoicing',
    url: 'invoices',
    icon: Wallet,
    visibleForGroups: [ROLES.SUPER_USER, ROLES.ACCOUNTING, ROLES.MANAGER],
  },
  // Anomalies intentionally NOT top-level — it's surfaced as an inline
  // button on the invoice list header to reduce sidebar clutter.
  {
    title: 'Expenses',
    url: 'expenses',
    icon: CreditCard,
    visibleForGroups: [ROLES.SUPER_USER, ROLES.ADMIN, ROLES.ACCOUNTING],
  },
  {
    title: 'Clients',
    url: 'clients',
    icon: Building2,
    visibleForGroups: [ROLES.SUPER_USER, ROLES.ADMIN, ROLES.ACCOUNTING, ROLES.MANAGER],
  },
  {
    title: 'Matters',
    url: 'matters',
    icon: Briefcase,
    visibleForGroups: [ROLES.SUPER_USER, ROLES.ADMIN, ROLES.ACCOUNTING, ROLES.MANAGER],
  },
  {
    title: 'Users',
    url: 'users',
    icon: User,
    visibleForGroups: [ROLES.SUPER_USER],
  },
  {
    title: 'AML Forms',
    url: 'aml-forms',
    icon: FileCheck,
    visibleForGroups: [ROLES.SUPER_USER, ROLES.ACCOUNTING, ROLES.MANAGER],
    requiresDomain: 'time.rblegal.eu',
  },
  // Audit is top-level and admin-only: it hosts both the activity log
  // and the invoice anomaly feed under a shared tab strip. Without a
  // sidebar entry the pages are effectively unreachable.
  {
    title: 'Audit',
    url: 'audit',
    icon: ShieldCheck,
    visibleForGroups: [ROLES.SUPER_USER, ROLES.ADMIN, ROLES.ACCOUNTING],
  },
  {
    title: 'Settings',
    url: 'settings',
    icon: Settings,
    visibleForGroups: [ROLES.SUPER_USER, ROLES.ADMIN, ROLES.ACCOUNTING, ROLES.HR, ROLES.MANAGER, ROLES.REGULAR],
  },
];

export function Sidebar() {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const [isReady, setIsReady] = useState(false);
  const userGroups = user?.groups || [];
  const currentDomain = window.location.hostname;
  
  useEffect(() => {
    // Only mark as ready when authentication is not loading and we have user data
    if (!loading && user) {
      setIsReady(true);
    }
  }, [loading, user]);

  const filteredMenuItems = MENU_ITEMS.filter((item) => {
    // Check if user has required group
    const hasRequiredGroup = hasAnyRole(userGroups, item.visibleForGroups);
    
    // Check domain requirement if specified
    if (item.requiresDomain) {
      return hasRequiredGroup && currentDomain === item.requiresDomain;
    }
    
    return hasRequiredGroup;
  });

  // Show nothing or a loading placeholder until authentication is ready
  if (!isReady) {
    return (
      <ShadcnSidebar className="border-r w-56 max-w-[240px] min-w-[200px]">
        <SidebarHeader>
          <SentraWordmark />
        </SidebarHeader>
        <Separator className="mx-2 mb-4" />
        <SidebarContent>
          <div className="px-3 py-2">Loading navigation...</div>
        </SidebarContent>
      </ShadcnSidebar>
    );
  }

  return (
    <ShadcnSidebar className="border-r w-56 max-w-[240px] min-w-[200px]">
        <SidebarHeader>
            <Link to="/dashboard" aria-label="Sentra home">
              <SentraWordmark />
            </Link>
        </SidebarHeader>
        
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <nav aria-label="Main navigation">
              <SidebarMenu className="gap-0.5">
                {filteredMenuItems.map((item) => {
                  const isActive = pathname === `/${item.url}` || pathname.startsWith(`/${item.url}/`);
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        className={cn(
                          "h-10 px-3 text-[15px] text-sidebar-foreground hover:bg-white/10 hover:text-white",
                          isActive && "bg-white/15 text-white hover:bg-white/15"
                        )}
                      >
                        <Link
                          to={`/${item.url}`}
                          aria-current={isActive ? 'page' : undefined}
                          className="flex items-center gap-3"
                        >
                          <item.icon className={cn("w-[18px] h-[18px] shrink-0", isActive ? "opacity-100" : "opacity-70")} />
                          <span className={cn("truncate", isActive ? "font-semibold" : "font-medium")}>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
              </nav>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </ShadcnSidebar>
  );
}
